import { defineStore } from "pinia";
import {
  AUTH_REFUSAL,
  authRefusalOf,
  decideAccessGate,
  createCompanyOrganisation,
  createProducerOrganisation,
  type CompanySignupFields,
  fetchMyAccess,
  fetchOrganisations,
  type ProducerSignupResult,
  fetchPermissions,
  mintOrgToken,
  readLastActiveOrgId,
  rememberLastActiveOrgId,
  resendVerificationEmail,
  type ResendVerificationResult,
  resolveAccessibleOrganisations,
  TenantAccessDeniedError,
} from "@/auth/session";
import { Auth0InvalidGrantError, handleRedirectCallback, loginWithRedirect, logout as auth0Logout } from "@/auth/auth0";
import { redeemPendingInvite } from "@/auth/invite";
import {
  decodeAuth0Identity,
  decodeJwtExpiryMs,
  decodeMetaShipUser,
  refreshAuth0TokensLocked,
  setStoredAuth0RefreshToken,
} from "@/auth/tokenStore";
import { RefreshScheduler } from "@/auth/refreshScheduler";
import {
  clearDeviceCredential,
  enrolThisDevice,
  tenantSupportsDeviceEnrolment,
  usableDeviceCredential,
  type DeviceCredential,
} from "@/device/deviceCredential";
import { DeviceSessionKeeper } from "@/device/deviceSession";
import { filterNavForTenant, type TenantNavModule } from "@/nav/navFilter";
import { isTenantUsable } from "@/tenant/availability";
import { markFirstSitePending } from "@/tenant/firstSite";
import { completePendingCompanySignup, offersCompanySignup } from "@/signup/companySignup";
import type { HostUser } from "@/state/globalState";
import type {
  NavBarItem,
  ResolveTenantAppResponse,
  TenantAppMyAccessResponse,
  UserOrganisation,
} from "@/tenant/types";
import type { TenantState } from "@tenant-theme";

export type SessionStatus =
  | "resolving"
  | "unavailable"
  | "signed-out"
  | "authenticating"
  | "org-pick"
  | "no-access"
  /**
   * DEV-937 — signed in, no organisation, and this app accepts sign-ups. The
   * farm form renders instead of "ask an administrator", which is a dead end
   * for the one visitor the app most wants.
   */
  | "needs-org"
  /**
   * PAC-1387 — signed in through Auth0 with an address that is not proven yet
   * (the auth handler's EMAIL_NOT_VERIFIED). Shows "Verify your email" with
   * Continue / Resend / different account, instead of the raw 403.
   */
  | "verify-email"
  | "ready"
  | "error";

interface SessionState {
  /** The hostname resolved against `GET /tenant-apps/resolve` (Appendix A2 carries no hostname field). */
  host: string;
  tenant: ResolveTenantAppResponse | null;
  status: SessionStatus;
  errorMessage: string | null;
  accessibleOrgs: UserOrganisation[];
  /**
   * PAC-1387 — whether the signed-in user belongs to ANY organisation at all
   * (before the tenant-app filter). NoAccess words itself differently for "you
   * have no company yet" vs "your company is not part of this app".
   * `null` = not known (e.g. a session resumed from storage); only an explicit
   * `false` earns the invite-only copy.
   */
  hasAnyOrganisation: boolean | null;
  currentOrg: UserOrganisation | null;
  user: HostUser | null;
  navItems: NavBarItem[];
  /**
   * Whether `navItems` is the live answer. `idle` = restored from a snapshot
   * (or nothing yet); `failed` = the last `/user/permission` fetch did not
   * land, and a retry is armed (see `navRetry`). The sidebar reads this so an
   * empty nav after a dropped connection says so, instead of showing "Home"
   * alone with no way back.
   */
  navStatus: NavStatus;
  /** Bridge bearer between Auth0 and an org selection — never persisted. */
  auth0Bearer: string | null;
  /** Current org-scoped Metaship token's `exp` claim (ms epoch) — drives the refresh scheduler. */
  tokenExpiryMs: number | null;
  /**
   * DEV-1110 — this session is running on the DEVICE credential rather than an
   * Auth0-derived user token: either the app opened with no signal, or Auth0
   * became unreachable mid-session. The session is real either way; what
   * changes is that it survives having no network, and the UI can say so.
   */
  deviceSession: boolean;
  /**
   * PAC-1388 — the reference of a company the visitor asked for on /signup
   * that went to review at their first sign-in. Onboarding shows it instead
   * of the form, the same panel the in-app form's review uses.
   */
  signupReviewReference: string | null;
}

/**
 * One refresh timer for the app's lifetime — module-scoped (not store state)
 * for the same reason `ApiV3.js` keeps `refreshPromise` at module scope: it's
 * a runtime handle, not serialisable session data, and there is exactly one
 * session in this shell.
 */
let refreshScheduler: RefreshScheduler | null = null;

/**
 * The device credential and its keeper live at module scope for the same reason
 * as `refreshScheduler` — runtime handles, not session data. The credential
 * additionally MUST NOT sit in reactive store state: it holds a 30-day secret,
 * and store state is the thing most likely to end up in a devtools snapshot or
 * an error report.
 */
let deviceKeeper: DeviceSessionKeeper | null = null;
let deviceCredential: DeviceCredential | null = null;

export type NavStatus = "idle" | "loading" | "ready" | "failed";

/** First nav retry delay after a failed fetch; doubles to `NAV_RETRY_MAX_MS`. */
export const NAV_RETRY_BASE_MS = 15 * 1000;
export const NAV_RETRY_MAX_MS = 5 * 60 * 1000;
/** Give up on one `/user/permission` attempt after this long, and retry. */
export const NAV_FETCH_TIMEOUT_MS = 20 * 1000;

/**
 * Re-fetch the nav after a failure until one lands. A phone that loses signal
 * mid-load used to keep an empty sidebar for the rest of the session: the
 * failure was swallowed and nothing ever asked again. `online` and
 * `visibilitychange` are the real triggers (same reasoning as
 * `DeviceSessionKeeper`); the backoff timer covers a captive portal or a 5xx,
 * where `online` never fires. Module-scoped like `refreshScheduler` — a runtime
 * handle, not session data.
 */
const navRetry = {
  failures: 0,
  timer: null as ReturnType<typeof setTimeout> | null,
  run: null as (() => void) | null,

  arm(run: () => void): void {
    this.failures += 1;
    if (!this.run) {
      window.addEventListener("online", this.onTrigger);
      document.addEventListener("visibilitychange", this.onTrigger);
    }
    this.run = run;
    if (this.timer) clearTimeout(this.timer);
    const delay = Math.min(NAV_RETRY_BASE_MS * 2 ** (this.failures - 1), NAV_RETRY_MAX_MS);
    this.timer = setTimeout(() => this.run?.(), delay);
  },

  disarm(): void {
    if (this.timer) clearTimeout(this.timer);
    this.timer = null;
    this.failures = 0;
    if (this.run) {
      window.removeEventListener("online", this.onTrigger);
      document.removeEventListener("visibilitychange", this.onTrigger);
    }
    this.run = null;
  },

  onTrigger(): void {
    if (document.visibilityState === "hidden" || navigator.onLine === false) return;
    navRetry.run?.();
  },
};

function auth0UserToHostUser(user: {
  sub: string;
  email: string;
  given_name?: string;
  family_name?: string;
  name?: string;
  picture?: string;
}): HostUser {
  const firstname = user.given_name || user.name?.split(" ")[0] || "";
  const surname = user.family_name || user.name?.split(" ").slice(1).join(" ") || "";
  return {
    id: 0, // placeholder — overwritten from the minted Metaship JWT the moment an org is selected (see selectOrganisation)
    email: user.email,
    firstname,
    surname,
    emailAddress: user.email,
    avatar: user.picture,
    auth0Id: user.sub,
  };
}

export const useSessionStore = defineStore("standalone-session", {
  state: (): SessionState => ({
    host: "",
    tenant: null,
    status: "resolving",
    errorMessage: null,
    accessibleOrgs: [],
    hasAnyOrganisation: null,
    currentOrg: null,
    user: null,
    navItems: [],
    navStatus: "idle",
    auth0Bearer: null,
    tokenExpiryMs: null,
    deviceSession: false,
    signupReviewReference: null,
  }),
  getters: {
    tenantModules(state): TenantNavModule[] {
      return state.tenant?.modules ?? [];
    },
  },
  actions: {
    /**
     * `isDevHost` lets a DRAFT/PROVISIONING/FAILED app be exercised via
     * `VITE_TENANT_HOST` on localhost before its provisioner run reaches
     * LIVE — see `tenant/availability.ts`. DISABLED/DELETED/`found:false`
     * stay Unavailable everywhere, dev included.
     */
    setTenant(host: string, tenant: ResolveTenantAppResponse, isDevHost = false) {
      this.host = host;
      this.tenant = tenant;
      this.status = isTenantUsable(tenant, isDevHost) ? "signed-out" : "unavailable";
      if (tenant.found && tenant.slug && tenant.kind && tenant.name && tenant.branding) {
        const tenantState: TenantState = {
          slug: tenant.slug,
          kind: tenant.kind,
          name: tenant.name,
          hostname: host,
          branding: tenant.branding,
        };
        window.MetaShipGlobalState?.updateState({ tenant: tenantState });
      }
    },

    /** Abandon an in-flight/failed auth0 callback and return to the sign-in screen. */
    returnToLogin() {
      this.errorMessage = null;
      this.auth0Bearer = null;
      this.status = "signed-out";
    },

    fail(message: string) {
      this.errorMessage = message;
      this.status = "error";
    },

    beginLogin(prompt?: string, screenHint?: string) {
      return loginWithRedirect({
        ...(prompt ? { prompt } : {}),
        ...(screenHint ? { screenHint } : {}),
      });
    },

    /** `/auth0/callback` → exchange the code, then resolve org access. */
    async completeAuth0Callback(search: string) {
      this.status = "authenticating";
      const result = await handleRedirectCallback(search);
      this.auth0Bearer = `Bearer ${result.token}`;
      this.user = auth0UserToHostUser(result.user);
      if (result.refreshToken) setStoredAuth0RefreshToken(result.refreshToken);
      // DEV-1044 — an invited grower has NO organisation until the invite gives
      // them one, so this must run before the access gate, not after it.
      // Best-effort: a failed redeem falls through to the normal gate rather
      // than stranding a legitimately signed-in user on an error.
      // PAC-1387 — not while the email is unverified: the redeem would be
      // refused and the one-shot invite token consumed with it. It stays parked
      // and is redeemed once Continue brings back a verified token.
      if (result.user.email_verified !== false) await redeemPendingInvite(this.auth0Bearer);
      await this.resolveOrgAccess();
    },

    /**
     * Merge the identity claims of an Auth0 id_token onto `user`, keeping any
     * numeric Metaship id already resolved (the id_token has no such claim —
     * only a minted Metaship token does, see `selectOrganisation`).
     */
    applyAuth0Identity(idToken: string | null | undefined) {
      const identity = decodeAuth0Identity(idToken);
      if (!identity) return;
      this.user = {
        id: this.user?.id ?? 0,
        email: identity.email || this.user?.email || "",
        firstname: identity.firstname || this.user?.firstname || "",
        surname: identity.surname || this.user?.surname || "",
        emailAddress: identity.email || this.user?.emailAddress || "",
        avatar: identity.avatar ?? this.user?.avatar ?? null,
        auth0Id: identity.sub || this.user?.auth0Id,
        tagId: this.user?.tagId ?? null,
      };
    },

    /**
     * Reload fast-path: the previous visit's Metaship token is still persisted
     * and unexpired on `window.MetaShipGlobalState` — skip the whole Auth0
     * round trip. Returns `false` (leaving `status` untouched) when there's
     * nothing usable, so the caller falls through to `resumeFromRefreshToken`.
     */
    resumeFromPersistedSession(): boolean {
      const persisted = window.MetaShipGlobalState?.getState();
      if (!persisted?.token || !persisted.orgToken || !persisted.currentOrg || !persisted.user) return false;
      if (persisted.tokenExpiry && Date.now() > persisted.tokenExpiry) return false;

      this.user = persisted.user;
      this.currentOrg = persisted.currentOrg;
      this.accessibleOrgs = persisted.organisations ?? [persisted.currentOrg];
      this.navItems = persisted.permissions ?? [];
      this.tokenExpiryMs = persisted.tokenExpiry ?? null;
      this.status = "ready";
      this.startRefreshScheduler();
      // The restored nav is a SNAPSHOT of whatever /user/permission returned on
      // a previous visit — it goes stale when the org's modules change, and an
      // empty snapshot (a failed fetch, or a shell build that filtered
      // everything out) would otherwise stick forever, leaving a sidebar with
      // nothing but "Home" in it. Re-fetch in the background; the restored list
      // stays on screen until the fresh one lands.
      void this.refreshNavigation(persisted.token, persisted.orgToken);
      return true;
    },

    /**
     * Re-fetch + re-filter the sidebar nav for the live token/org. Silent by
     * design: this runs on a session that is ALREADY usable, so a failure must
     * leave the visitor exactly where they are rather than surfacing an error.
     */
    async refreshNavigation(bearer?: string | null, orgToken?: string | null): Promise<void> {
      const persisted = window.MetaShipGlobalState?.getState();
      const activeBearer = bearer ?? persisted?.token ?? null;
      const activeOrgToken = orgToken ?? persisted?.orgToken ?? null;
      if (!activeBearer || !activeOrgToken) return;
      if (this.navStatus === "loading") return;
      this.navStatus = "loading";
      try {
        // One bar of signal can leave a fetch open for minutes; without a cap,
        // the in-flight guard above would hold every retry off behind it.
        const navItems = await Promise.race([
          fetchPermissions(activeBearer, activeOrgToken),
          new Promise<never>((_, reject) =>
            setTimeout(() => reject(new Error("/user/permission timed out")), NAV_FETCH_TIMEOUT_MS),
          ),
        ]);
        // Signed out (or switched org) while the request was in flight — the
        // answer belongs to a session that no longer exists.
        if (this.status !== "ready") return;
        this.navItems = filterNavForTenant(navItems, this.tenantModules);
        this.navStatus = "ready";
        navRetry.disarm();
        window.MetaShipGlobalState?.updateState({ permissions: this.navItems });
      } catch {
        // Keep the restored nav — a transient /user/permission failure is not
        // a reason to empty the sidebar. But DO ask again: the token is read
        // fresh from global state on each retry, so a token that rotated in
        // the meantime (refresh scheduler, device keeper) is the one used.
        this.navStatus = "failed";
        if (this.status === "ready") navRetry.arm(() => void this.refreshNavigation());
      }
    },

    /** The sidebar's "Retry" — try now, not on the backoff schedule. */
    retryNavigation(): void {
      void this.refreshNavigation();
    },

    /**
     * Silent resume from a stored Auth0 refresh token (page reload with no
     * live Metaship token). The refresh token is only ever discarded when
     * Auth0 itself rejects it (`Auth0InvalidGrantError`) — a downstream
     * failure (e.g. `/user/permission` 500, a network blip) leaves it intact
     * and just surfaces an error, since the credential itself is still good.
     */
    async resumeFromRefreshToken() {
      this.status = "authenticating";

      // A previous visit may have persisted `user` (with its real numeric
      // Metaship id) even though its token has now expired — rehydrate it so
      // `resolveOrgAccess`'s lastActiveOrg lookup uses the numeric id instead
      // of falling back to having no hint at all.
      const persistedUser = window.MetaShipGlobalState?.getState()?.user;
      if (persistedUser?.id) this.user = persistedUser;

      let fresh;
      try {
        fresh = await refreshAuth0TokensLocked();
      } catch (e) {
        if (e instanceof Auth0InvalidGrantError) setStoredAuth0RefreshToken(null);
        this.status = "signed-out";
        throw e;
      }
      if (!fresh.id_token) {
        this.status = "signed-out";
        throw new Error("Auth0 refresh returned no id_token");
      }
      this.auth0Bearer = `Bearer ${fresh.id_token}`;
      // A silent resume never touches the userinfo response `completeAuth0Callback`
      // reads, so the identity has to come out of the id_token itself. Without
      // this the session ran all the way to "ready" with `user === null`: the top
      // bar showed a "?" monogram, every module reading `user.id` off the global
      // state got nothing, and `readLastActiveOrgId(this.user.id)` below could
      // never fire — so a multi-org visitor was sent to the organisation picker
      // on every single visit.
      this.applyAuth0Identity(fresh.id_token);

      // Same as the callback path: someone who already had a session in this
      // tab can still be arriving on an invite link.
      if (decodeAuth0Identity(fresh.id_token)?.emailVerified !== false) {
        await redeemPendingInvite(this.auth0Bearer);
      }

      try {
        await this.resolveOrgAccess();
      } catch (e) {
        this.fail(e instanceof Error ? e.message : "Could not restore your session");
        throw e;
      }
    },

    /**
     * DEV-937 — create the organisation, then walk the normal post-login path.
     *
     * On success the caller is a member of a brand-new (or freshly claimed)
     * organisation, so `resolveOrgAccess` now finds one and auto-selects it —
     * reusing the same token-mint and permission-load every other sign-in runs,
     * rather than a second bespoke path that would drift from it.
     */
    async completeSignup(farm: {
      farmName: string;
      contactName?: string;
      contactPhone?: string;
      province?: string;
    }): Promise<ProducerSignupResult> {
      if (!this.tenant?.slug) throw new Error("completeSignup called before the tenant resolved");
      const bearer = await this.ensureAuth0Bearer();
      const result = await createProducerOrganisation(this.tenant.slug, bearer, farm);

      // NEEDS_REVIEW means a human has to decide (a claim on an organisation
      // that already has members, or a duplicate business id). There is nothing
      // to sign into yet, so the caller shows the reference instead.
      if (result.outcome === "NEEDS_REVIEW") return result;

      await this.resolveOrgAccess();
      return result;
    },

    /**
     * PAC-1374 — the company form's twin of `completeSignup`: create or claim
     * the company, then the same post-login path. On success the WMS module is
     * told (once, via sessionStorage) that this org's first job is its first
     * site POI, so it opens site creation instead of an empty dashboard.
     */
    async completeCompanySignup(company: CompanySignupFields): Promise<ProducerSignupResult> {
      if (!this.tenant?.slug) throw new Error("completeCompanySignup called before the tenant resolved");
      const bearer = await this.ensureAuth0Bearer();
      const result = await createCompanyOrganisation(this.tenant.slug, bearer, company);
      if (result.outcome === "NEEDS_REVIEW") return result;

      if (result.organisationId != null) markFirstSitePending(result.organisationId);
      await this.resolveOrgAccess();
      return result;
    },

    /**
     * PAC-1388 — an orgless visitor on a WMS front door may be arriving from
     * the /signup page's set-password email. If the backend holds a pending
     * signup for their (now verified) email, it creates the company from it;
     * true means there is an organisation to resolve again. Never blocks
     * sign-in: any failure falls back to the in-app company form.
     */
    async completePendingSignupIfAny(): Promise<boolean> {
      if (!this.tenant?.slug || !this.auth0Bearer || !offersCompanySignup(this.tenant)) return false;
      try {
        const result = await completePendingCompanySignup(this.tenant.slug, this.auth0Bearer);
        if (result.outcome === "NEEDS_REVIEW") {
          this.signupReviewReference = result.reference ?? "";
          return false;
        }
        if (result.outcome === "NO_PENDING_SIGNUP" || result.organisationId == null) return false;
        markFirstSitePending(result.organisationId);
        return true;
      } catch {
        return false;
      }
    },

    /**
     * PAC-1387 — "I've clicked the link": force a FRESH Auth0 token (the one in
     * hand still says `email_verified: false` — Auth0 only re-reads the profile
     * when it mints a new one) and walk the normal post-login path again.
     *
     * Returns false when the new token is still unverified (the link has not
     * been opened yet) — the screen stays and says so. With no refresh token to
     * renew from, re-runs the Auth0 redirect instead: its SSO session is live,
     * so that comes straight back with a newly-minted token.
     */
    async continueAfterEmailVerification(): Promise<boolean> {
      let fresh;
      try {
        fresh = await refreshAuth0TokensLocked();
      } catch (e) {
        if (e instanceof Auth0InvalidGrantError) setStoredAuth0RefreshToken(null);
        await loginWithRedirect();
        return false;
      }
      if (!fresh.id_token) {
        await loginWithRedirect();
        return false;
      }
      this.auth0Bearer = `Bearer ${fresh.id_token}`;
      this.applyAuth0Identity(fresh.id_token);
      if (decodeAuth0Identity(fresh.id_token)?.emailVerified === false) return false;

      // The invite parked while unverified (see completeAuth0Callback).
      await redeemPendingInvite(this.auth0Bearer);
      await this.resolveOrgAccess();
      // resolveOrgAccess can still land on verify-email if the backend
      // disagrees with the claim — report that honestly.
      return this.status !== "verify-email";
    },

    /** PAC-1387 — ask the backend to re-send Auth0's verification email to this identity. */
    async resendEmailVerification(): Promise<ResendVerificationResult> {
      const bearer = await this.ensureAuth0Bearer();
      return resendVerificationEmail(bearer);
    },

    /** organisations → my-access filter → gate decision (auto-select / picker / NoAccess). */
    async resolveOrgAccess(afterPendingSignup = false) {
      if (!this.tenant?.slug || !this.auth0Bearer) {
        throw new Error("resolveOrgAccess called before tenant/auth0Bearer set");
      }
      let allOrgs: UserOrganisation[];
      let myAccess: TenantAppMyAccessResponse;
      try {
        [allOrgs, myAccess] = await Promise.all([
          fetchOrganisations(this.auth0Bearer),
          fetchMyAccess(this.tenant.slug, this.auth0Bearer),
        ]);
      } catch (e) {
        // PAC-1387 — the auth handler's two sign-in refusals each have a screen.
        const refusal = authRefusalOf(e);
        if (refusal === AUTH_REFUSAL.EMAIL_NOT_VERIFIED) {
          this.errorMessage = null;
          this.status = "verify-email";
          return;
        }
        // A brand-new, verified Auth0 identity has no Metaship user yet, so the
        // auth handler 401s. To the visitor that is simply "no organisation":
        // sign-up where the app takes them, "ask for an invite" where not. The
        // sign-up endpoints create the user themselves.
        if (refusal !== AUTH_REFUSAL.ACCOUNT_NOT_FOUND) throw e;
        allOrgs = [];
        myAccess = { found: false, kind: null, organisationIds: [] };
      }
      this.hasAnyOrganisation = allOrgs.length > 0;
      this.accessibleOrgs = resolveAccessibleOrganisations(allOrgs, myAccess);

      const lastActiveOrgId = this.user?.id != null ? readLastActiveOrgId(this.user.id) : null;
      const decision = decideAccessGate(this.accessibleOrgs, lastActiveOrgId);
      if (decision.mode === "none") {
        // PAC-1388: first sign-in after the /signup page — finish that company,
        // then resolve once more (once: a second empty answer is the form).
        if (!afterPendingSignup && (await this.completePendingSignupIfAny())) {
          await this.resolveOrgAccess(true);
          return;
        }
        // An orgless visitor is the SIGN-UP case on an app that accepts them,
        // and only a dead end on one that does not.
        this.status = this.tenant?.selfServeSignup === true ? "needs-org" : "no-access";
        return;
      }
      if (decision.mode === "pick") {
        this.status = "org-pick";
        return;
      }
      await this.selectOrganisation(decision.org);
    },

    /**
     * Silently renew the Auth0 credential if it's missing or close to
     * expiring, so the org switcher still works after a plain page reload
     * (`resumeFromPersistedSession` never sets `auth0Bearer` — it restores
     * straight from the persisted Metaship token, which has nothing to do
     * with the Auth0 side).
     */
    async ensureAuth0Bearer(): Promise<string> {
      if (this.auth0Bearer) {
        const expMs = decodeJwtExpiryMs(this.auth0Bearer);
        if (expMs == null || Date.now() < expMs) return this.auth0Bearer;
      }
      let fresh;
      try {
        fresh = await refreshAuth0TokensLocked();
      } catch (e) {
        if (e instanceof Auth0InvalidGrantError) setStoredAuth0RefreshToken(null);
        throw e;
      }
      if (!fresh.id_token) throw new Error("Auth0 refresh returned no id_token");
      this.auth0Bearer = `Bearer ${fresh.id_token}`;
      return this.auth0Bearer;
    },

    /** Mint the org-scoped Metaship token, fetch + filter the nav, and mark the session ready. */
    async selectOrganisation(org: UserOrganisation) {
      if (!this.tenant?.slug) throw new Error("selectOrganisation called before tenant set");
      const auth0Bearer = await this.ensureAuth0Bearer();

      let minted;
      try {
        minted = await mintOrgToken(org, auth0Bearer, this.tenant.slug);
      } catch (e) {
        // Server-side truth: this org is not part of the app, even though
        // my-access said otherwise (revoked between calls) — same terminal
        // state as the my-access empty-list case (binding design addendum).
        if (e instanceof TenantAccessDeniedError) {
          this.denyAccess();
          return;
        }
        throw e;
      }
      if (!minted?.token) throw new Error("Organisation token mint refused");

      const bearer = `Bearer ${minted.token}`;
      const navItems = await fetchPermissions(bearer, org.token);
      const expiryMs = decodeJwtExpiryMs(bearer);

      // The MINTED Metaship JWT (not the Auth0 id_token) carries the real
      // numeric Metaship user id — the Auth0 userinfo placeholder (id: 0)
      // never resolves to it on its own. Without this, `user.id` stayed 0
      // forever (published to every module reading it — orders/api.ts,
      // bookings/bookingWizard.ts, finance-v2/hub.ts) and the `if (this.user
      // ?.id)` guard below was permanently falsy, so lastActiveOrg was NEVER
      // written.
      //
      // The `&& this.user` half of the old guard was the second half of the
      // same bug: on any path that reached here WITHOUT an Auth0 identity, the
      // minted token's own `{ id, firstname, surname, emailAddress }` was
      // thrown away too and the session stayed anonymous. The token is a
      // complete identity on its own — use it as the base when there is none.
      const metaShipUser = decodeMetaShipUser(bearer);
      if (metaShipUser) {
        this.user = {
          id: metaShipUser.id,
          email: metaShipUser.emailAddress || this.user?.email || "",
          firstname: metaShipUser.firstname || this.user?.firstname || "",
          surname: metaShipUser.surname || this.user?.surname || "",
          emailAddress: metaShipUser.emailAddress || this.user?.emailAddress || "",
          avatar: this.user?.avatar ?? null,
          auth0Id: this.user?.auth0Id,
          tagId: metaShipUser.tagId,
        };
      }

      this.currentOrg = org;
      this.navItems = filterNavForTenant(navItems, this.tenantModules);
      this.navStatus = "ready";
      navRetry.disarm();
      this.tokenExpiryMs = expiryMs;
      this.status = "ready";
      if (this.user?.id != null) rememberLastActiveOrgId(this.user.id, org.id);

      window.MetaShipGlobalState?.updateState({
        token: bearer,
        tokenExpiry: expiryMs,
        orgToken: org.token,
        currentOrg: org,
        organisations: this.accessibleOrgs,
        permissions: this.navItems,
        user: this.user,
      });

      this.startRefreshScheduler();

      // DEV-1110 — enrol this device off the session we just established, so
      // the NEXT cold start works with no signal. Not awaited: a grower waiting
      // on their farm must never wait on an enrolment call, and it cannot fail
      // in a way that matters (see `enrolDevice`).
      void this.enrolDevice(bearer, org.token);

      return { bearer, orgToken: org.token, expiryMs };
    },

    /**
     * Proactive refresh: silently renew the Auth0 credential from the stored
     * rotating refresh token, re-mint the org-scoped Metaship token, and
     * republish `token`/`tokenExpiry`/`orgToken` to the global state (+ its
     * `vuex` mirror) in one atomic `updateState` call. Ported from
     * `ApiV3.js` `refreshAccessToken` (:96-120) + `auth0Config.js`
     * `refreshAuth0Tokens` (:304-327) — ONE difference: this shell has no
     * per-request interceptor to trigger it, so `RefreshScheduler` calls it
     * on a timer instead of on the next outgoing request.
     *
     * Any thrown error here (Auth0-rejected, transient network, tenant
     * access denied) is classified by `RefreshScheduler`/`denyAccess`, NOT
     * by this method — it just does the work and lets failures propagate.
     */
    async refreshSession(): Promise<void> {
      if (!this.currentOrg) throw new Error("refreshSession called with no active organisation");
      if (!this.tenant?.slug) throw new Error("refreshSession called before tenant set");

      const fresh = await refreshAuth0TokensLocked();
      if (!fresh.id_token) throw new Error("Auth0 refresh returned no id_token");

      let minted;
      try {
        minted = await mintOrgToken(this.currentOrg, `Bearer ${fresh.id_token}`, this.tenant.slug);
      } catch (e) {
        if (e instanceof TenantAccessDeniedError) {
          // Access to this app was revoked mid-session. `denyAccess()` stops
          // the scheduler (so it does NOT also see the now-null tokenExpiryMs
          // and race a second, full sign-out on top of this one) and swaps to
          // NoAccess — swallow rather than rethrow so RefreshScheduler's
          // generic catch doesn't ALSO call signOut() over this.
          this.denyAccess();
          return;
        }
        throw e;
      }
      if (!minted?.token) throw new Error("Organisation token refresh refused");

      const bearer = `Bearer ${minted.token}`;
      this.tokenExpiryMs = decodeJwtExpiryMs(bearer);

      window.MetaShipGlobalState?.updateState({
        token: bearer,
        tokenExpiry: this.tokenExpiryMs,
        orgToken: this.currentOrg.token,
      });
    },

    /** Idempotent — safe to call every time the session becomes ready (boot resume, org switch). */
    startRefreshScheduler() {
      refreshScheduler?.stop();
      refreshScheduler = new RefreshScheduler({
        getExpiryMs: () => this.tokenExpiryMs,
        refresh: () => this.refreshSession(),
        // DEV-1110: an enrolled device does NOT get signed out because Auth0
        // became unreachable — that is the exact moment it is supposed to keep
        // working. See `signOutOrFallBackToDevice`.
        signOut: (clearRefreshToken) => void this.signOutOrFallBackToDevice(clearRefreshToken),
      });
      refreshScheduler.start();
    },

    stopRefreshScheduler() {
      refreshScheduler?.stop();
      refreshScheduler = null;
    },

    /* ── DEV-1110 · the device credential ──────────────────────────────────── */

    /**
     * Enrol this device for the signed-in grower, so the next cold start works
     * with no signal. Called on every successful org selection: the backend
     * rotates the same row rather than adding one, and a rotation is how the
     * secret stays fresh.
     *
     * Best-effort by construction (`enrolThisDevice` never throws) — a failed
     * enrolment leaves a perfectly good online session alone.
     */
    async enrolDevice(bearer: string, orgToken: string): Promise<void> {
      if (this.deviceSession) return; // already ON the device credential
      if (!this.user?.id || !this.currentOrg) return;
      // The shell serves every tenant app; only the farm app has an offline
      // story, a Settings → Devices screen, and `boer:*` scopes to be minted
      // against. Enrolling anywhere else would mint a 30-day credential nobody
      // can see or revoke.
      if (!tenantSupportsDeviceEnrolment(this.tenantModules)) return;

      const credential = await enrolThisDevice({
        bearer,
        orgToken,
        userId: this.user.id,
        organisationId: this.currentOrg.id,
      });
      if (credential) deviceCredential = credential;
    },

    /**
     * Continue THIS session on the device credential. Returns false when there
     * is nothing to continue with, leaving the caller to sign out as it would
     * have.
     */
    async switchToDeviceSession(): Promise<boolean> {
      if (!this.user?.id || !this.currentOrg) return false;
      const credential = await usableDeviceCredential(this.user.id, this.currentOrg.id);
      if (!credential) return false;

      this.stopRefreshScheduler();
      deviceCredential = credential;
      this.deviceSession = true;
      this.status = "ready";
      this.startDeviceKeeper();
      return true;
    },

    /**
     * Cold boot with no usable Auth0 path: rebuild the session from the last
     * visit's snapshot and this device's credential.
     *
     * The identity comes from `MetaShipGlobalState` (persisted across reloads),
     * NOT from the network — that is the whole point. The credential only has
     * to prove the snapshot still belongs to this device and this user.
     */
    async resumeFromDeviceCredential(): Promise<boolean> {
      const persisted = window.MetaShipGlobalState?.getState();
      if (!persisted?.user?.id || !persisted.currentOrg) return false;

      const restore = {
        user: this.user,
        currentOrg: this.currentOrg,
        accessibleOrgs: this.accessibleOrgs,
        navItems: this.navItems,
      };
      this.user = persisted.user;
      this.currentOrg = persisted.currentOrg;
      this.accessibleOrgs = persisted.organisations ?? [persisted.currentOrg];
      this.navItems = persisted.permissions ?? [];

      if (await this.switchToDeviceSession()) return true;

      // No credential for this user+device: put the store back exactly as it
      // was, so the caller's own "signed-out" handling is untouched.
      this.user = restore.user;
      this.currentOrg = restore.currentOrg;
      this.accessibleOrgs = restore.accessibleOrgs;
      this.navItems = restore.navItems;
      return false;
    },

    /**
     * Hold the device session open: exchange the credential for a 12h token
     * whenever there is signal, renew the 30 days while we have both, and —
     * critically — never end the session just because the network is gone.
     */
    startDeviceKeeper() {
      deviceKeeper?.stop();
      deviceKeeper = new DeviceSessionKeeper({
        getCredential: () => deviceCredential,
        setCredential: (credential) => {
          deviceCredential = credential;
        },
        onEvent: (event) => {
          if (event.type === "token") {
            this.tokenExpiryMs = event.expiry;
            window.MetaShipGlobalState?.updateState({
              token: event.bearer,
              tokenExpiry: event.expiry,
              orgToken: this.currentOrg?.token ?? null,
            });
            // A device session starts from the last visit's nav snapshot,
            // which may be empty or stale. The first live token is the first
            // moment it can be checked.
            if (this.navStatus !== "ready" && this.currentOrg?.token) {
              void this.refreshNavigation(event.bearer, this.currentOrg.token);
            }
            return;
          }
          if (event.type === "rejected") {
            // Revoked in Settings, or past its 30 days. The Auth0 refresh token
            // may still be good, so this is a LOCAL sign-out: the next load can
            // still resume silently online rather than demanding a full login.
            void this.endDeviceSession();
            this.signOut({ clearRefreshToken: false });
          }
          // `offline` is not an error and needs no handling — the session
          // stands, the module works from its cache, and the keeper will try
          // again the moment the phone sees signal.
        },
      });
      deviceKeeper.start();
    },

    /** Stop the keeper and forget the credential — local only. */
    async endDeviceSession(): Promise<void> {
      deviceKeeper?.stop();
      deviceKeeper = null;
      deviceCredential = null;
      this.deviceSession = false;
      await clearDeviceCredential();
    },

    /**
     * The refresh scheduler wants to end the session. For an enrolled device,
     * that is usually wrong: `decideRefreshFailure` reaches "sign out" when the
     * token has expired and Auth0 could not be reached — which is precisely a
     * grower in a block, and precisely what the device credential is for.
     *
     * `clearRefreshToken: true` means Auth0 EXPLICITLY rejected the credential
     * (`invalid_grant`), which is a real sign-out and is honoured as one.
     */
    async signOutOrFallBackToDevice(clearRefreshToken: boolean): Promise<void> {
      if (!clearRefreshToken && (await this.switchToDeviceSession())) return;
      this.signOut({ clearRefreshToken });
    },

    /**
     * Server said this org is not part of the app (a `TenantAccessDeniedError`
     * from `POST /user-access/refresh-token`) — route to NoAccess, same
     * terminal UI as the my-access empty-list case, and locally terminate the
     * live session (clear org/nav/token, wipe the global state so mounted
     * modules lose their credentials) WITHOUT the full Auth0 `/v2/logout`
     * redirect — that stays reserved for the user's own "Sign out" click on
     * the NoAccess screen.
     */
    denyAccess() {
      this.stopRefreshScheduler();
      // DEV-1110 — this organisation is no longer part of this app, so its
      // device credential has no business opening it offline either. Local
      // only: the `apiCredential` row itself is not tenant-app-scoped, so
      // revoking app access does NOT revoke device credentials server-side.
      // Worth closing properly when tenant-app access becomes a credential
      // concern; until then this at least stops THIS device.
      void this.endDeviceSession();
      this.currentOrg = null;
      this.accessibleOrgs = [];
      this.navItems = [];
      this.navStatus = "idle";
      navRetry.disarm();
      this.tokenExpiryMs = null;
      // PAC-1387 — a revoked member HAS an organisation; never let NoAccess
      // mistake them for an orgless visitor and show the invite-only copy.
      this.hasAnyOrganisation = true;
      this.status = "no-access";
      window.MetaShipGlobalState?.updateState({
        token: null,
        tokenExpiry: null,
        orgToken: null,
        currentOrg: null,
        organisations: null,
        permissions: null,
      });
    },

    /**
     * `clearRefreshToken` defaults to `true` for every explicit, user-facing
     * call (the "Sign out" button). `RefreshScheduler` passes `false` for a
     * transient-failure sign-out — the stored refresh token is still good,
     * so a fresh page load can silently resume instead of forcing a full
     * re-login (see `decideRefreshFailure`).
     */
    signOut(options: { clearRefreshToken?: boolean } = {}) {
      const clearRefreshToken = options.clearRefreshToken ?? true;
      this.stopRefreshScheduler();
      // DEV-1110 — an EXPLICIT sign-out takes the device credential with it:
      // leaving a 30-day credential on a phone whose owner just signed out is
      // exactly the surprise the de-authorise screen exists to prevent. A
      // transient-failure sign-out (`clearRefreshToken: false`) deliberately
      // does NOT — that case is "the network is gone", which is what the
      // credential is for. `switchToDeviceSession` will have caught it first.
      if (clearRefreshToken) {
        void this.endDeviceSession();
      } else {
        deviceKeeper?.stop();
        deviceKeeper = null;
        this.deviceSession = false;
      }
      this.currentOrg = null;
      this.user = null;
      this.navItems = [];
      this.navStatus = "idle";
      navRetry.disarm();
      this.accessibleOrgs = [];
      this.hasAnyOrganisation = null;
      this.auth0Bearer = null;
      this.tokenExpiryMs = null;
      this.status = "signed-out";
      window.MetaShipGlobalState?.updateState({
        token: null,
        tokenExpiry: null,
        orgToken: null,
        currentOrg: null,
        organisations: null,
        permissions: null,
        user: null,
      });
      if (clearRefreshToken) {
        // A genuine, final sign-out (explicit click, or Auth0 itself
        // rejected the credential) — discard the refresh token AND bounce
        // through Auth0's own logout. A transient-failure give-up (RT still
        // possibly good, see `decideRefreshFailure`) only resets LOCAL
        // state: forcing an external navigation on top of a network issue
        // would be actively worse, and the kept RT lets the next page load
        // silently resume instead of demanding a full re-login.
        setStoredAuth0RefreshToken(null);
        auth0Logout();
      }
    },
  },
});
